import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import { AuthContextProvider } from './hooks/useAuth';
import { ModalContextProvider } from './hooks/useModal';
import { UnsavedChangesProvider } from './hooks/useUnsavedChanges';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { i18nConfig } from './i18n';
import { CssBaseline, ThemeProvider } from '@mui/material';
import theme from './theme';
import StripeContext from './pages/bookings/components/payment/StripeContext';

const CustomThemeProvider = ({ children }) => {
    const customTheme = theme();
    return <ThemeProvider theme={customTheme}>{children}</ThemeProvider>;
};

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // Data is considered immediately stale
            staleTime: 0,

            // Disable background serving of old data
            gcTime: 0, // (optional: removes cache entirely)

            // Always refetch when component mounts or window refocuses
            refetchOnMount: true,
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,

            // Disable "keepPreviousData"
            keepPreviousData: false,
            retry: false,
        },
    },
});

const Providers = ({ children }) => {
    return (
        <QueryClientProvider client={queryClient}>
            <CustomThemeProvider>
                <CssBaseline />
                <BrowserRouter>
                    <SnackbarProvider preventDuplicate>
                        <StripeContext>
                            <I18nextProvider i18n={i18nConfig}>
                                <AuthContextProvider>
                                    <ModalContextProvider>
                                        <UnsavedChangesProvider>{children}</UnsavedChangesProvider>
                                    </ModalContextProvider>
                                </AuthContextProvider>
                            </I18nextProvider>
                        </StripeContext>
                    </SnackbarProvider>
                </BrowserRouter>
            </CustomThemeProvider>
        </QueryClientProvider>
    );
};

export default Providers;
